import React, {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useHistory} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {Auth, Hub} from 'aws-amplify';

import store from './../../store';
import * as MovieActions from '../../store/actions/movie';

import styled, {css} from 'styled-components';
import {
    useBreakpoint,
    mobileMediaQuery,
    notWebMediaQuery,
    PageBodyWrapper,
    Flex,
} from './../../styles';
import {currentEnv, isMobileDevice, useIsCartoonito} from '../../helpers';

import BigButton from './../BigButton';
import Button from './../Button';
import Dropdown from '../Dropdown';
import {LinkButton} from '../DrawerMenu';
import Onboarding from './Onboarding';
import CartoonitoHeader from './CartoonitoHeader';

import {ReactComponent as CNLogo} from './../../assets/cn-logo.svg';
import {ReactComponent as KeyIcon} from './../../assets/key_icon.svg';
import Home from './../../assets/ic_cn_home.png';
import HomeSelected from './../../assets/ic_cn_home_selected.png';
import Shows from './../../assets/ic_cn_shows.png';
import ShowsSelected from './../../assets/ic_cn_shows_selected.png';
import Movies from './../../assets/popcorn_icon.svg';
import Now from './../../assets/ic_cn_onnow.png';
import NowSelected from './../../assets/ic_cn_onnow_selected.png';
import Games from './../../assets/ic_cn_games.png';
import GamesSelected from './../../assets/ic_cn_games_selected.png';
import Favourites from './../../assets/ic_cn_favorites.png';
import FavouritesSelected from './../../assets/ic_cn_favorites_selected.png';
import ProfileIcon from '../../assets/ic_burger_menu.png';
import {ReactComponent as Chevron} from './../../assets/arrow.svg';
import ExitIcon from '../../assets/icon-exit.png';
import CartoonitoLogo from '../../assets/cnito_icon.png';
import Blob from '../Blob';

const Container = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    background-color: black;
    width: 100%;
    height: 80px;
    font-size: 32px;
    color: #fff !important;
    z-index: 100;
    top: 0;
    box-sizing: border-box;
    transition: 0.3s background ease-in-out;

    ${({transparentHeader}) =>
        transparentHeader &&
        css`
            ${notWebMediaQuery} {
                background: linear-gradient(
                    180deg,
                    rgba(0, 0, 0, 0.6) 0%,
                    rgba(0, 0, 0, 0) 100%
                );
            }
        `}

    padding: 0;

    ${({backgroundColor}) => backgroundColor && css`
            
        background: ${backgroundColor};
        ${notWebMediaQuery} {
            background: ${backgroundColor};
        }
    `}

    ${notWebMediaQuery} {
        height: 78px;
    }

    ${mobileMediaQuery} {
        padding: 16.5px 0;
    }

    ${({headerTop}) =>
        headerTop &&
        isMobileDevice() &&
        css`
            margin-top: ${headerTop || 0}px;
        `}
`;

const Wrapper = styled(PageBodyWrapper)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 80px;
    max-height: 100%;
    box-sizing: border-box;
    padding: 0 40px;

    ${mobileMediaQuery} {
        padding: 0 25px;
    }
`;

const MainLogo = styled.div`
    padding: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 105px;
    cursor: pointer;
`;

const ButtonsRow = styled.nav`
    height: 100%;
    width: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: auto;
    button {
        flex: 1;
    }

    ${notWebMediaQuery} {
        display: none;
    }
`;

const ChevronIcon = styled(Chevron)`
    margin-left: -7px;
    width: 28px;
    height: 28px;
    margin: 0px;

    transform: scaleX(-1);
    path {
        fill: #fff;
    }
`;

const LoginButton = styled(Button)`
    svg {
        padding-bottom: 4px;
        margin-left: 12px;
        path {
            fill: #000;
        }
    }
`;

const CartoonitoLandingHeader = styled(Flex)`
    z-index: 100;
    top: 0;
    box-sizing: border-box;
    transition: background-color 0.3s ease;
`;

const AppHeader = ({setShowDrawerMenu, ...props}) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const breakpoint = useBreakpoint();

    const isMobile = ['xs', 'mobile'].includes(breakpoint);
    const isTablet = ['sm', 'md'].includes(breakpoint);

    const {t} = useTranslation();
    const pathname = props.currentPathname || history.location.pathname;

    const user = useSelector(state => state.user);
    const config = useSelector(state => state.config);
    const isCartoonito = useIsCartoonito();

    const idp = user && user.idp;

    const [transparentHeader, setTransparentHeader] = useState(
        window.pageYOffset < 64,
    );

    const [headerTop, setHeaderTop] = useState(
        document.querySelector('.smart-banner') && isMobileDevice()
            ? 56 - window.pageYOffset
            : 0,
    );
    const [federatedSignInProvider, setFederatedSignInProvider] = useState({
        provider: 'WMID',
    });
    const [movies, setMovies] = useState(undefined);

    const movie = useSelector(state => state.movie);

    useEffect(() => {
        if (
            user.catalogTenant &&
            (!movie.movies || (movie.movies && movie.movies.length <= 0))
        ) {
            dispatch(MovieActions.loadMovies());
        }

        // eslint-disable-next-line
    }, [user.catalogTenant]);

    useEffect(() => {
        if (movie.movies !== undefined) {
            setMovies(movie.movies);
        }

        // eslint-disable-next-line
    }, [movie.movies]);

    useEffect(() => {
        Hub.listen('auth', data => {
            let config = store.getState().config;

            setFederatedSignInProvider(
                config.auth_settings &&
                    config.auth_settings.oidc &&
                    config.auth_settings.oidc.federatedSignIn
                    ? config.auth_settings.oidc.federatedSignIn
                    : {
                          provider: 'WMID',
                      },
            );
        });

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        handleWindowScroll();
        window.addEventListener('scroll', handleWindowScroll);

        return () => {
            window.removeEventListener('scroll', handleWindowScroll);
        };

        // eslint-disable-next-line
    }, [window.location.pathname]);

    const handleWindowScroll = () => {
        const distanceFromTop = 56 - window.pageYOffset;

        if (isMobileDevice() && distanceFromTop > 0) {
            setHeaderTop(distanceFromTop);
        } else {
            setHeaderTop(0);
        }

        if (window.pageYOffset < 64) {
            setTransparentHeader(true);
        } else {
            setTransparentHeader(false);
        }
    };

    const handleLogout = () => {
        dispatch({
            type: 'MODAL_SHOW',
            modal: {
                visible: true,
                title: t('logout.message'),
                buttons: (
                    <>
                        <Button
                            margin={
                                isMobile || isTablet
                                    ? '0 1rem 0 auto'
                                    : '0 1rem 0 auto'
                            }
                            onClick={handleConfirmLogout}
                        >
                            {t('logout.confirm')}
                        </Button>
                        <Button
                            margin={(isMobile || isTablet) && '0 auto 0 0'}
                            onClick={() => dispatch({type: 'MODAL_CLOSE'})}
                        >
                            {t('logout.cancel')}
                        </Button>
                    </>
                ),
            },
        });
    };

    const handleConfirmLogout = () => {
        dispatch({type: 'MODAL_CLOSE'});

        localStorage.clear();

        Auth.signOut()
            .then(() => {
                localStorage.clear();
                window.location.replace('/');
            })
            .catch(() => {
                localStorage.clear();
                window.location.replace('/');
            });
    };

    const handleLogin = () => {
        window.turner_metadata.trackAction.push({
            type: 'interaction',
            data: {
                interaction: 'cnplus|login start',
                domain: 'cnplus.com',
                brand: 'cartoon network',
                interactionevent: '1',
                authstate:
                    user && user.jwtToken ? 'authenticated' : 'unauthenticated',
                mvpd: idp || 'idp not loaded',
            },
        });

        if (federatedSignInProvider !== undefined) {
            setTimeout(() => {
                Auth.federatedSignIn(federatedSignInProvider);
            }, 1500);
        }
    };

    const backHeader = () => {
        let showBackButton = false;

        if (
            history.location.pathname.indexOf('/play') >= 0 ||
            history.location.pathname.indexOf('/now/') >= 0 ||
            history.location.pathname.indexOf('/shows/') >= 0
        ) {
            showBackButton = true;
        }

        return showBackButton && (isMobile || isTablet);
    };

    const handleGoToCNAPP = () => {
        if (currentEnv !== 'prd') {
            history.push('/home');
        } else {
            window.location.href = 'https://cnapp.cartoonnetwork.com.br';
        }
    };

    if (history.location.pathname === '/onboarding') {
        return <Onboarding transparentHeader={transparentHeader} />;
    }

    if (isCartoonito) {
        return <CartoonitoHeader pathname={pathname} />;
    }

    if (
        isCartoonito ||
        ['/cartoonito', '/programacao', '/programacion'].includes(
            history.location.pathname,
        )
    ) {
        return (
            <CartoonitoLandingHeader
                position="fixed"
                width="100%"
                padding="15px"
                tablet
                desktop
                justifyContent="space-between"
                background={transparentHeader ? 'transparent' : 'black'}
            >
                <img src={CartoonitoLogo} alt="Cartoonito Logo" />
                <Button onClick={handleGoToCNAPP}>
                    {t('cartoonito.header.cnappCTA')}
                </Button>
            </CartoonitoLandingHeader>
        );
    }

    //if in favorites and tablet or smaller, we change the header to #211921
    let bgColor = null;
    if(pathname === '/favourites' && ['xs', 'mobile', 'sm', 'md'].includes(breakpoint)) {
        bgColor = '#211921';
    }

    return (
        <Container
            className="header"
            transparentHeader={transparentHeader}
            headerTop={headerTop}
            backgroundColor={bgColor}
        >
            <Wrapper>
                <Blob />
                {backHeader() ? (
                    <>
                        <ChevronIcon
                            title={t('controls.goback')}
                            onClick={() => history.goBack()}
                        />
                    </>
                ) : (
                    <>
                        <MainLogo onClick={() => history.push('/')}>
                            <CNLogo
                                title="Cartoon Network"
                                aria-hidden="true"
                                width={
                                    isMobile
                                        ? '81px'
                                        : isTablet
                                        ? '77px'
                                        : '99px'
                                }
                                height={
                                    isMobile
                                        ? '48px'
                                        : isTablet
                                        ? '45px'
                                        : '58px'
                                }
                            />
                        </MainLogo>

                        {((config.btw && config.btw.enabled) ||
                            (user && user.jwtToken)) && (
                            <ButtonsRow>
                                <BigButton
                                    as={Link}
                                    active={['/', '/home'].includes(pathname)}
                                    icon={
                                        ['/', '/home'].includes(pathname)
                                            ? HomeSelected
                                            : Home
                                    }
                                    to="/home"
                                    value={t('home.title')}
                                    iconWidth="28px"
                                    iconHeight="28px"
                                    title={t('home.title')}
                                />
                                <BigButton
                                    as={Link}
                                    active={pathname.indexOf('/shows') >= 0}
                                    icon={
                                        pathname.indexOf('/shows') >= 0
                                            ? ShowsSelected
                                            : Shows
                                    }
                                    to="/shows"
                                    value={t('shows.title')}
                                    iconWidth="28x"
                                    iconHeight="28px"
                                    title={t('shows.title')}
                                />

                                {false && movies && movies.length > 0 && (
                                    <BigButton
                                        as={Link}
                                        active={
                                            pathname.indexOf('/movies') >= 0
                                        }
                                        icon={Movies}
                                        to="/movies"
                                        value={t('movies.title')}
                                        iconWidth="28px"
                                        iconHeight="28px"
                                        title={t('movies.title')}
                                    />
                                )}
                                <BigButton
                                    as={Link}
                                    active={pathname.indexOf('/now') >= 0}
                                    icon={
                                        pathname.indexOf('/now') >= 0
                                            ? NowSelected
                                            : Now
                                    }
                                    to="/now"
                                    value={t('onnow.title')}
                                    iconWidth="28px"
                                    iconHeight="28px"
                                    title={t('onnow.title')}
                                />
                                <BigButton
                                    as={Link}
                                    active={pathname.indexOf('/games') >= 0}
                                    icon={
                                        pathname.indexOf('/games') >= 0
                                            ? GamesSelected
                                            : Games
                                    }
                                    to="/games"
                                    value={t('games.title')}
                                    iconWidth="28px"
                                    iconHeight="28px"
                                    title={t('games.title')}
                                />

                                {[null, 'xl'].includes(breakpoint) &&
                                    user.jwtToken && (
                                        <BigButton
                                            as={Link}
                                            active={
                                                pathname === '/favourites'
                                                    ? 1
                                                    : 0
                                            }
                                            icon={
                                                pathname === '/favourites'
                                                    ? FavouritesSelected
                                                    : Favourites
                                            }
                                            to="/favourites"
                                            value={t('favorites.title')}
                                            iconWidth="28px"
                                            iconHeight="28px"
                                            title={t('favorites.title')}
                                        />
                                    )}
                            </ButtonsRow>
                        )}

                        {isMobile || isTablet ? (
                            <BigButton
                                title={t('controls.profile')}
                                minWidth="fit-content"
                                flex="0"
                                margin="0"
                                iconWidth="28px"
                                iconHeight="28px"
                                padding="0"
                                width="auto"
                                icon={ProfileIcon}
                                onClick={() => setShowDrawerMenu(true)}
                            />
                        ) : user.jwtToken ? (
                            <Dropdown
                                hideCaret
                                backdrop
                                dark
                                borderRadius="4px"
                                width="254px"
                                top="42px"
                                value={
                                    <BigButton
                                        as="div"
                                        title={t('controls.profile')}
                                        minWidth="fit-content"
                                        flex="0"
                                        margin="0"
                                        iconWidth="28px"
                                        iconHeight="28px"
                                        padding="0"
                                        width="auto"
                                        icon={ProfileIcon}
                                    />
                                }
                                options={[
                                    ![null, 'xl'].includes(breakpoint) &&
                                        user.jwtToken && (
                                            <LinkButton
                                                as={Link}
                                                to="/favourites"
                                            >
                                                <img
                                                    alt={t('favorites.title')}
                                                    src={Favourites}
                                                />
                                                {t('favorites.title')}
                                            </LinkButton>
                                        ),
                                    <LinkButton onClick={handleLogout}>
                                        <img
                                            alt={t('drawer.logout')}
                                            src={ExitIcon}
                                            width="22px"
                                            height="22px"
                                        />
                                        {t('drawer.logout')}
                                    </LinkButton>,
                                ]}
                            />
                        ) : (
                            <>
                                <LoginButton medium light onClick={handleLogin}>
                                    Login
                                    <KeyIcon
                                        title={'Login'}
                                        aria-hidden="true"
                                        width="22px"
                                        height="22px"
                                    />
                                </LoginButton>
                            </>
                        )}
                    </>
                )}
            </Wrapper>
        </Container>
    );
};

export default AppHeader;
